import React from "react";
import { Banner, Button, FormField, Input } from "@/ds";
import { cockpit } from "@/api";
import { pathOf } from "../router";
import { LanguageSwitcher, useT } from "../i18n";
import { LoginBrandpane, PASSWORD_RESET_DONE_PARAM } from "./screen-login";
import { isThrottled, retryAfterMinutes, tokenFromHash } from "./invitation.logic";

/**
 * Self-service password reset (#1478): asking for a link, and spending it.
 *
 * Both screens sit in the sign-in frame, because that is where the person came from and where they
 * return. Neither one can say whether an account exists: the request screen shows the same answer
 * for every address, and a link that cannot be spent is refused without saying why.
 */

const MIN_PASSWORD = 10;

function PreauthFrame({ testId, children }: { testId: string; children: React.ReactNode }) {
  return (
    <div className="login" data-testid={testId}>
      <LoginBrandpane />
      <div className="preauth__lang"><LanguageSwitcher light /></div>
      <div className="login__formpane">
        <div className="login__form">{children}</div>
      </div>
    </div>
  );
}

function ToLogin() {
  const t = useT();
  return (
    <p className="login__assist login__assist--start">
      <a href={pathOf({ name: "login" })}>{t("login.toLogin")}</a>
    </p>
  );
}

type RequestOutcome =
  | { kind: "idle" }
  | { kind: "sent" }
  | { kind: "throttled"; minutes: number }
  | { kind: "invalidEmail" }
  | { kind: "failed" };

export function PasswordForgotScreen() {
  const t = useT();
  const [email, setEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [outcome, setOutcome] = React.useState<RequestOutcome>({ kind: "idle" });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await cockpit.requestPasswordReset(email.trim());
      setOutcome({ kind: "sent" });
    } catch (error: unknown) {
      if (isThrottled(error)) setOutcome({ kind: "throttled", minutes: retryAfterMinutes(error) });
      else if ((error as { status?: unknown })?.status === 400) setOutcome({ kind: "invalidEmail" });
      else setOutcome({ kind: "failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PreauthFrame testId="password-forgot-screen">
      <form onSubmit={submit}>
        <h1>{t("login.forgot.title")}</h1>
        <p className="login__lede">{t("login.forgot.sub")}</p>
        {outcome.kind !== "idle" && (
          <div className="login__notice" data-testid="password-forgot-outcome">
            {outcome.kind === "sent" && (
              <Banner tone="info" title={t("login.forgot.sentTitle")}>
                {t("login.forgot.sentBody")}
              </Banner>
            )}
            {outcome.kind === "throttled" && (
              <Banner tone="warning" title={t("login.throttledTitle")}>
                {t("login.forgot.throttledBody", { minutes: outcome.minutes })}
              </Banner>
            )}
            {outcome.kind === "invalidEmail" && (
              <Banner tone="danger" title={t("login.forgot.failedTitle")}>
                {t("login.forgot.invalidEmail")}
              </Banner>
            )}
            {outcome.kind === "failed" && (
              <Banner tone="danger" title={t("login.forgot.failedTitle")}>
                {t("login.forgot.failedBody")}
              </Banner>
            )}
          </div>
        )}
        <div className="stack-sm">
          <FormField label={t("login.email")} htmlFor="pr-mail">
            <Input
              id="pr-mail"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
          </FormField>
          <Button loading={busy} type="submit" fullWidth disabled={busy || !email.trim()} aria-busy={busy}>
            {outcome.kind === "sent" ? t("login.forgot.resend") : t("login.forgot.submit")}
          </Button>
        </div>
        <ToLogin />
      </form>
    </PreauthFrame>
  );
}

type ResetPhase =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "throttled"; minutes: number }
  | { kind: "form"; email: string };

export function PasswordResetScreen() {
  const t = useT();
  // Captured once: the fragment is the only copy of the token, and it is never written back.
  const [token] = React.useState(() => tokenFromHash(window.location.hash));
  const [phase, setPhase] = React.useState<ResetPhase>({ kind: "loading" });
  const [password, setPassword] = React.useState("");
  const [repeat, setRepeat] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let live = true;
    if (!token) {
      setPhase({ kind: "invalid" });
      return;
    }
    cockpit.verifyPasswordReset(token).then(
      (link) => { if (live) setPhase({ kind: "form", email: link.email }); },
      (e: unknown) => {
        if (!live) return;
        setPhase(isThrottled(e) ? { kind: "throttled", minutes: retryAfterMinutes(e) } : { kind: "invalid" });
      },
    );
    return () => { live = false; };
  }, [token]);

  const mismatch = password !== "" && repeat !== "" && password !== repeat;
  const ready = password.length >= MIN_PASSWORD && password === repeat;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await cockpit.confirmPasswordReset({ token, password });
      // A full replace: the spent token leaves the address bar and this history entry with it.
      window.location.replace(`${pathOf({ name: "login" })}?${PASSWORD_RESET_DONE_PARAM}`);
    } catch (e: unknown) {
      setBusy(false);
      if (isThrottled(e)) setError("login.reset.throttled");
      else if ((e as { status?: unknown })?.status === 404) setPhase({ kind: "invalid" });
      else setError("login.reset.failed");
    }
  };

  if (phase.kind === "loading") {
    return (
      <PreauthFrame testId="password-reset-loading">
        <p className="subtle" role="status">{t("login.reset.loading")}</p>
      </PreauthFrame>
    );
  }

  if (phase.kind === "throttled") {
    return (
      <PreauthFrame testId="password-reset-throttled">
        <h1>{t("login.reset.title")}</h1>
        <Banner tone="warning" title={t("login.throttledTitle")}>
          {t("login.reset.throttledBody", { minutes: phase.minutes })}
        </Banner>
        <ToLogin />
      </PreauthFrame>
    );
  }

  if (phase.kind === "invalid") {
    return (
      <PreauthFrame testId="password-reset-invalid">
        <h1>{t("login.reset.title")}</h1>
        <div className="login__notice">
          <Banner tone="danger" title={t("login.resetInvalid.title")}>
            {t("login.resetInvalid.body")}
          </Banner>
        </div>
        <p className="login__assist login__assist--start">
          <a href={pathOf({ name: "passwordForgot" })}>{t("login.resetInvalid.requestNew")}</a>
        </p>
        <ToLogin />
      </PreauthFrame>
    );
  }

  return (
    <PreauthFrame testId="password-reset-screen">
      <form onSubmit={submit}>
        <h1>{t("login.reset.title")}</h1>
        <p className="login__lede">{t("login.reset.sub")}</p>
        {error && (
          <div className="login__notice">
            <Banner tone="danger" title={t("common.error")}>{t(error)}</Banner>
          </div>
        )}
        <div className="stack-sm">
          {/* Read-only, and present so a password manager files the new password under the right
              account. The server takes the account from the link, never from this field. */}
          <FormField label={t("login.email")} htmlFor="pr-account">
            <Input id="pr-account" type="email" value={phase.email} readOnly autoComplete="username" />
          </FormField>
          <FormField label={t("login.reset.passwordLabel")} htmlFor="pr-password">
            <Input
              id="pr-password"
              type="password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
              required
            />
          </FormField>
          <p className="login__hint">{t("login.reset.passwordHint")}</p>
          <FormField label={t("login.reset.repeatLabel")} htmlFor="pr-repeat">
            <Input
              id="pr-repeat"
              type="password"
              value={repeat}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRepeat(e.target.value)}
              autoComplete="new-password"
              error={mismatch}
              required
            />
          </FormField>
          {mismatch && <p className="login__hint" role="alert">{t("login.reset.mismatch")}</p>}
          <Button loading={busy} type="submit" fullWidth disabled={busy || !ready} aria-busy={busy}>
            {t("login.reset.submit")}
          </Button>
        </div>
        <ToLogin />
      </form>
    </PreauthFrame>
  );
}
